import { useCallback, useState } from 'react'
import type { HttpTrigger } from '~/prix/actionDefinition'
import useAPI from '~/prix/react/hooks/api'
import { apiBasePath } from '~/utils/shared/platform'
import variableItemsExportDefinition from '~/packages/general/variable/export/variableItemsExport.definition'
import type { Expression } from '~/prix/types/expression'
import type { Space, Time } from '~/packages/general/variable/spaceTime.types'
import type { Variable } from '~/packages/general/variable/variables.typing'
import resolveCurrentRequestToken from './resolveCurrentRequestToken'

export type VariableItemsExportFormat = 'xlsx' | 'csv'

export type VariableItemsExportInput = {
  variable: Variable
  filter?: Expression | null
  highlight?: Expression | null
  space?: Space | null
  time?: Time | null
  select?: string[]
  maxItems?: number
}

export interface ExportResult {
  blob: Blob
  filename: string
}

export default function useVariableItemsExport() {
  const { context } = useAPI()
  const { bypassAuth, getToken, token: contextToken } = context
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const exportVariableItems = useCallback(
    async (
      input: VariableItemsExportInput,
      format: VariableItemsExportFormat,
    ): Promise<ExportResult> => {
      if (isLoading) {
        throw new Error('Export já em andamento')
      }

      setIsLoading(true)
      setError(null)

      const trigger = variableItemsExportDefinition.triggers.find(
        (value): value is HttpTrigger => value.trigger === 'http',
      )

      if (!trigger) {
        const finalError = new Error('Trigger HTTP não encontrado')
        setError(finalError)
        setIsLoading(false)
        throw finalError
      }

      const url = `${apiBasePath}${trigger.route}`
      const token = await resolveCurrentRequestToken({
        bypassAuth,
        getToken,
        token: contextToken,
      })
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Token: token } : {}),
      }

      try {
        const response = await fetch(url, {
          method: trigger.method,
          headers,
          body: JSON.stringify({
            ...input,
            format,
          }),
        })

        if (!response.ok) {
          const message = await response.text()
          throw new Error(message || 'Erro ao exportar itens')
        }

        const contentDisposition = response.headers.get('Content-Disposition')
        const match = contentDisposition?.match(/filename=\"?([^\";]+)\"?/)
        const filename =
          (match && decodeURIComponent(match[1])) ||
          `itens-${new Date().toISOString().split('T')[0]}.${format}`

        const blob = await response.blob()
        return { blob, filename }
      } catch (error) {
        console.error('[useVariableItemsExport] Export error', error)
        const finalError =
          error instanceof Error ? error : new Error('Erro ao exportar itens')
        setError(finalError)
        throw finalError
      } finally {
        setIsLoading(false)
      }
    },
    [bypassAuth, contextToken, getToken, isLoading],
  )

  return {
    exportVariableItems,
    isLoading,
    error,
  }
}
